<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="$class(headerState)"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
    >
      <pre-header-banner
        v-if="showPreHeaderBanner"
        :pre-header-banner-content="preHeaderBannerContent"
      />
      <div
        :is-search-active="search.active"
        class="site-header__masthead"
      >
        <div class="site-header__container">
          <div class="site-header__navigation--left-side">
            <!-- Menu icon -->
            <button
              class="site-header__menu icon-button"
              type="button"
              :aria-label="$string('general.site_header.menu')"
              @click="toggleOverlay({
                component: 'menu-drawer',
                ignoreDismissed: true,
                namespace: 'menuDrawer',
              })"
            >
              <span
                class="site-header__hamburger hamburger"
                :class="{ [$class('active')]: getActiveOverlay === 'menuDrawer' }"
              >
                <span class="hamburger__inner" />
              </span>
            </button>

            <!-- Search icon -->
            <button
              v-if="!$variable('nonTransactional.hide')"
              class="
                site-header__navigation-link
                site-header__navigation-link--button
                site-header__navigation-link--mobile
              "
              :aria-label="createAriaLabel('search')"
              type="button"
              @click="handleSearchFocus"
            >
              <icon-search />
            </button>
          </div>

          <!-- Logo Mobile -->
          <a
            class="site-header__logo site-header__logo--mobile"
            :aria-label="$string('general.site_header.return_home')"
            :href="$variable('routes.root')"
          >

            <component
              :is="titleElement"
              id="site-header-title"
              class="site-header__logo-image"
              v-html="$variable('store.logo_primary')"
            />

          </a>

          <!-- Navigation -->
          <nav class="site-header__navigation">
            <ul class="site-header__navigation-list">
              <li
                v-for="(tier1) of desktop.items"
                :key="tier1.handle"
                class="site-header__navigation-item"
              >
                <button
                  class="
                    site-header__navigation-link
                    site-header__navigation-link--button
                  "
                  type="button"
                  @click.prevent="openMenuDrawerWithTier(tier1)"
                >
                  <span
                    class="text-heading-s"
                    v-text="tier1.title"
                  />

                  <icon-plus />
                </button>
              </li>
            </ul>
          </nav>

          <!-- Logo Desktop -->
          <a
            class="site-header__logo site-header__logo--desktop"
            :href="$variable('routes.root')"
            :aria-label="$string('general.site_header.return_home')"
          >
            <component
              :is="titleElement"
              id="site-header-title desktop"
              class="site-header__logo-image is-alt"
              v-html="$variable('store.logo_secondary')"
            />
          </a>

          <div
            v-if="!$variable('nonTransactional.hide')"
            class="site-header__navigation--right-side"
          >
            <search-bar
              class="site-header__navigation-link search-bar--desktop"
              :redirect="search.redirect"
              :search-query="search.query"
              @search-focus="handleSearchFocus"
              @search-input="(input) => search.query = input"
              @clear="clearSearch"
            />

            <!-- Account icon -->
            <a
              class="site-header__navigation-link site-header__navigation-link--desktop"
              :href="$variable('routes.account.url')"
              :aria-label="createAriaLabel('account')"
            >
              <icon-account />
            </a>

            <!-- Wishlist icon -->
            <a
              v-if="$variable('routes.wishlist')"
              class="site-header__navigation-link"
              :href="$variable('routes.wishlist')"
              :aria-label="createAriaLabel('wishlist')"
            >
              <icon-wishlist />
            </a>

            <!-- Cart icon -->
            <a
              class="site-header__cart icon-button"
              :href="$variable('routes.cart.url')"
              :aria-label="$string('general.site_header.cart')"
              @click="handleCartToggle"
            >
              <icon-cart />

              <span
                class="site-header__cart-badge"
                aria-hidden="true"
                v-text="cart.item_count"
              />
            </a>
          </div>
        </div>
      </div>
    </header>

    <!-- Menu drawer -->
    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :type="isMobile ? 'drawer' : 'fade'"
      :background-image="backgroundImage"
      :template="false"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          ref="menuDrawer"
          namespace="menuDrawer"
          :active-tier="activeTier"
          :handle="mobile.handle"
          :is-active="isActive"
          :item-count="mobile.itemCount"
        />
      </template>
    </overlay>

    <!-- Predictive search -->
    <transition
      :name="isMobile ? 'slide-right' : 'slide-down'"
    >
      <div
        v-if="search.active"
        class="predictive-search-overlay site-header__predictive-search"
      >
        <div
          v-if="isMobile"
          class="predictive-search__drawer-header"
        >
          <p
            class="predictive-search__title text-heading-l-sans"
            v-text="$string('search.drawer_title')"
          />

          <search-bar
            class="predictive-search__search-bar search-bar__search"
            :redirect="search.redirect"
            :search-query="search.query"
            @search-input="(input) => search.query = input"
            @clear="clearSearch"
          />
        </div>

        <predictive-search
          v-if="getComponentVisible('predictive-search')"
          v-cloak
          :is-active="search.active"
          :query="search.query"
          :helpful-links="helpfulLinks"
          :popular-searches="popularSearches"
          :search-settings="searchSettings"
          @update-query="(query) => search.query = query"
          @update-redirect="(redirect) => search.redirect = redirect"
        />

        <predictive-search-loading
          v-if="!getComponentActive('predictive-search')"
          :helpful-links="helpfulLinks"
          :popular-searches="popularSearches"
          class="predictive-search container grid"
        />
      </div>
    </transition>

    <overlay
      key="predictiveSearch"
      direction="up"
      namespace="predictiveSearch"
      no-content
      type="modal"
    />
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {Object} helpfulLinks - Predictive search helpful links settings.
 * @param {Object} popularSearches - Predictive search popular searches.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import PreHeaderBanner from '~global/site-header/pre-header-banner'
import PredictiveSearch from '~async/predictive-search/predictive-search'
import PredictiveSearchLoading from '~async/predictive-search/predictive-search-loading'
import SearchBar from '~global/search-bar/search-bar'

import IconAccount from '~icons/misc/account.svg'
import IconCart from '~icons/misc/cart.svg'
import IconPlus from '~icons/misc/plus.svg'
import IconSearch from '~icons/misc/search.svg'
import IconWishlist from '~icons/misc/heart.svg'

export default {
  name: 'SiteHeader',

  components: {
    Btn,
    PreHeaderBanner,
    IconAccount,
    IconCart,
    IconPlus,
    IconSearch,
    IconWishlist,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    PredictiveSearch,
    PredictiveSearchLoading,
    SearchBar,
    Overlay,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    preHeaderBannerContent: {
      type: Array,
      default: () => [],
    },
    searchSettings: {
      type: Object,
      default: () => ({
        popularSearches: [],
        trendingProducts: [],
      }),
    },
    helpfulLinks: {
      type: Array,
      default: () => [],
    },
    popularSearches: {
      type: Array,
      default: () => [],
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
  },

  data() {
    return {
      height: {
        total: 0,
        visible: 0,
      },
      activeTier: false,
      search: {
        active: false,
        query: '',
        redirect: '',
        focus: '',
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
      backgroundImage: (state) => state['menu-drawer'].backgroundImage,
    }),

    /**
     * Render pre-header banner if content and visibility of them provided.
     * @returns {boolean}
     */
    showPreHeaderBanner() {
      return this.preHeaderBannerContent.length > 0
    },
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'translate') {
          return
        }

        this.setHeights()
      }
    },

    /**
    * Watch active overlay to disable search/navigation tiers when it closes.
    */
    getActiveOverlay() {
      if (this.getActiveOverlay !== 'predictiveSearch') {
        this.search.active = false
        this.search.query = ''
      }
    },

    /**
     * Watch isMobile to update heights.
     */
    isMobile() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights(true)
        return
      }

      this.$refs.siteHeader.ontransitionend = () => {
        this.setHeights(true)
      }
    },

    /**
     * Watch search active to open/close associated overlay.
     */
    'search.active'() {
      if (this.search.active) {
        this.openOverlay({
          component: 'predictive-search',
          ignoreDismissed: true,
          namespace: 'predictiveSearch',
        })

        return
      }

      this.closeOverlay('predictiveSearch')
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible'() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },
  },

  mounted() {
    nextTick().then(() => {
      this.setHeights(true)
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Set heights of fixed header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      /**
       * Visible height is element height combined with translateY value.
       */
      this.height.visible =
        this.$refs.siteHeader.offsetHeight +
        this.$refs.siteHeader.getBoundingClientRect()?.y
    },

    /**
     * Helper to consyruct an aria-label
     * @param {String} link- String identifying the icon/button.
     * @returns {String} - Constructed aria-label string.
     */
    createAriaLabel(link) {
      return `${this.$string('accessibility.open')} ${link} ${this.$string('general.site_header.icon_button')}`
    },

    /**
     * Handle search focus.
     * - Prevents predicitve search opening on search page.
     * @param {Boolean} active - Search is active?
     */
    handleSearchFocus(active) {
      this.search.active = active
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer') ||
        this.cart.item_count === 0
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    openMenuDrawerWithTier(tier) {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })

      if (this.$refs?.menuDrawer) {
        this.$refs.menuDrawer.findAndSetCurrentLink(tier)

        return
      }

      this.activeTier = tier
    },

    clearSearch() {
      this.search.query = ''
    },
  },
}
</script>
